import * as React from 'react'
  /* @jsx mdx */
import { mdx } from '@mdx-js/react';
/* @jsxRuntime classic */
/* @jsx mdx */
import { LeadParagraph } from '../../components/LeadParagraph';
export const _frontmatter = {
  "pageTitle": "Toggle API",
  "title": "Toggle",
  "props": ["ToggleProps"]
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
  console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope");
  return <div {...props} />;
};
const PageContent = makeShortcode("PageContent");
const Link = makeShortcode("Link");
const Alert = makeShortcode("Alert");
const ToggleProps = makeShortcode("ToggleProps");
const layoutProps = {
  _frontmatter
};
const MDXLayout = "wrapper";
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <PageContent componentName="toggle" type="api" mdxType="PageContent">
      <LeadParagraph mdxType="LeadParagraph">
  Toggles (also called Switches) are form elements used to set a single item
  either an on or off value.
      </LeadParagraph>
      <p>{`See also: `}<Link to="/api/checkboxes" mdxType="Link">{`Checkboxes`}</Link>{` and `}<Link to="/api/radio" mdxType="Link">{`Radio Buttons`}</Link>{`.`}</p>
      <h2>{`Basic Usage`}</h2>
      <p>{`The label/toggle association will be handled via the passed in `}<inlineCode parentName="p">{`id`}</inlineCode>{` if one is supplied or via an auto-generated `}<inlineCode parentName="p">{`id`}</inlineCode>{` if not supplied.`}</p>
      <p>{`Toggles can either be controlled or uncontrolled. If you pass in a checked prop, it is a controlled input. That means that to change the toggle's state, you must updated the checked prop's value. If you do not want to have your toggle be in a controlled state, but need the initial checked value to be set, pass in the defaultChecked prop with the value you would like on initial render.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Toggle, FormGroup } from 'react-magma-dom';

export function Example() {
  const [checked, updateChecked] = React.useState<boolean>(false);

  function handleUpdateChecked() {
    updateChecked(!checked);
  }

  return (
    <FormGroup labelText="Toggle one or more">
      <Toggle defaultChecked={true} labelText="Uncontrolled Toggle" />
      <Toggle
        checked={checked}
        labelText="Controlled toggle"
        onChange={handleUpdateChecked}
      />
    </FormGroup>
  );
}
`}</code></pre>
      <h2>{`Disabled`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Toggle } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Toggle disabled labelText="Disabled toggle" />
      <Toggle disabled checked labelText="Disabled toggle on" />
    </>
  );
}
`}</code></pre>
      <h2>{`Error Message`}</h2>
      <p>{`An `}<inlineCode parentName="p">{`errorMessage`}</inlineCode>{` can also be supplied to an toggle to indicate the selection is invalid.`}</p>
      <p>{`The `}<inlineCode parentName="p">{`required`}</inlineCode>{` attribute can be used on a toggle if that field is required.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Toggle } from 'react-magma-dom';

export function Example() {
  const [hasError, setHasError] = React.useState(true);

  const message = hasError ? 'You must turn permissions on' : null;

  function handleChange(e) {
    setHasError(!e.target.checked);
  }

  return (
    <Toggle
      errorMessage={message}
      labelText="Enable Permissions"
      onChange={handleChange}
      required
    />
  );
}
`}</code></pre>
      <h2>{`Text Position`}</h2>
      <p>{`Use the optional prop `}<inlineCode parentName="p">{`textPosition`}</inlineCode>{` to position the label text on either side of the toggle switch.
The `}<inlineCode parentName="p">{`textPosition`}</inlineCode>{` property takes the values `}<inlineCode parentName="p">{`left`}</inlineCode>{` and `}<inlineCode parentName="p">{`right`}</inlineCode>{`. The default value is `}<inlineCode parentName="p">{`left`}</inlineCode>{`.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Toggle } from 'react-magma-dom';

export function Example() {
  return (
    <>
      <Toggle labelText="Text left" />
      <Toggle labelText="Text right" textPosition="right" />
    </>
  );
}
`}</code></pre>
      <h2>{`Visually Hidden Label`}</h2>
      <p>{`Adding the `}<inlineCode parentName="p">{`isTextVisuallyHidden`}</inlineCode>{` prop will make the label text available to screen readers, but will hide it visually.`}</p>
      <Alert variant="warning" mdxType="Alert">
  Use caution when using the <inlineCode>isTextVisuallyHidden</inlineCode> prop;
  it should only be used in rare circumstances when the purpose of the toggle is
  conveyed in another way. While screen readers will announce the label when the
  toggle gains focus, it is still best to have a visual label. This is
  especially helpful for individuals with cognitive disabilities and users who
  have fine motor control limitations.
      </Alert>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Toggle } from 'react-magma-dom';

export function Example() {
  return <Toggle labelText="Label is hidden" isTextVisuallyHidden />;
}
`}</code></pre>
      <h2>{`Custom Styles`}</h2>
      <p>{`Custom styles can be passed into both the Toggle component. The `}<inlineCode parentName="p">{`containerStyle`}</inlineCode>{` property will apply to the container.
Additional `}<inlineCode parentName="p">{`labelStyle`}</inlineCode>{`, `}<inlineCode parentName="p">{`trackStyle`}</inlineCode>{` and `}<inlineCode parentName="p">{`thumbStyle`}</inlineCode>{` properties are available to style the respective elements.
Please use discretion when adding custom styles.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Toggle } from 'react-magma-dom';

export function Example() {
  return (
    <Toggle
      containerStyle={{ border: '1px dotted purple', width: '100%' }}
      labelStyle={{ fontStyle: 'italic', flexGrow: '1' }}
      labelText="Custom styles"
      thumbStyle={{
        background: 'yellow',
        border: '1px solid orange',
        boxShadow: '0 0 0',
      }}
      trackStyle={{ borderRadius: '5px', height: '24px' }}
    />
  );
}
`}</code></pre>
      <h2>{`Inverse`}</h2>
      <pre><code parentName="pre" {...{
          "className": "language-tsx"
        }}>{`import React from 'react';
import { Toggle, Card, CardBody } from 'react-magma-dom';

export function Example() {
  return (
    <Card isInverse>
      <CardBody>
        <Toggle isInverse labelText="Inverse toggle" />
        <Toggle isInverse disabled checked labelText="Inverse disabled" />
      </CardBody>
    </Card>
  );
}
`}</code></pre>
      <h2>{`Testing`}</h2>
      <p>{`Passing in the `}<inlineCode parentName="p">{`testId`}</inlineCode>{` prop will add the `}<inlineCode parentName="p">{`data-testid`}</inlineCode>{` attribute to the input element for easier querying in tests.`}</p>
      <pre><code parentName="pre" {...{
          "className": "language-html",
          "metastring": "noCodeSandbox",
          "noCodeSandbox": true
        }}>{`<Toggle labelText="Toggle label" testId="toggle-testid" />
`}</code></pre>
      <h2>{`Toggle Props`}</h2>
      <p><strong parentName="p">{`This component uses `}<inlineCode parentName="strong">{`forwardRef`}</inlineCode>{`. The ref is applied to the `}<inlineCode parentName="strong">{`input`}</inlineCode>{` element.`}</strong></p>
      <p>{`All of the `}<a parentName="p" {...{
          "href": "https://developer.mozilla.org/en-US/docs/Web/HTML/Element/input"
        }}>{`standard input attributes`}</a>{` can be provided as props and will be applied to the `}<inlineCode parentName="p">{`input`}</inlineCode>{` element that gets rendered.`}</p>
      <ToggleProps mdxType="ToggleProps" />
    </PageContent>

    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;
      